import { MoveMoneyOptions, ZendeskArticle } from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, StyleSheet, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import MoveMoneyOptionsQuery from 'services/graphql/queries/MoveMoney/MoveMoneyOptions';
import { HTMLRenderer } from 'shared/components/HTMLRenderer/HTMLRenderer';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import LinkButton from 'shared/components/vxl/LinkButton/LinkButton';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { showZendeskArticle } from 'shared/utils/zendesk/zendeskUtils';
import { useQuery } from 'urql';

interface IMoveMoneyOptions {
  moveMoneyOptions: MoveMoneyOptions;
}

const CompareOptionsScreen = () => {
  const router = useRouter();
  const [{ data, fetching, error }] = useQuery<IMoveMoneyOptions>({
    query: MoveMoneyOptionsQuery,
    requestPolicy: 'cache-and-network',
  });

  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'compareOptions',
  });

  const { bottom } = useSafeAreaInsets();

  const moveMoneyOptionList = data?.moveMoneyOptions?.options;

  const { defaultTitle, defaultMessage, cancelButtonTitle } =
    useDefaultErrorStrings();

  useViewDidFocusScreenAnalyticsEvent({
    key: 'moveMoneyOptions',
  });

  const cancel = () => {
    router.back();
  };
  const buttons = {
    primaryButton: {
      title: cancelButtonTitle,
      onPress: cancel,
    },
  };

  const errorModel = error?.message
    ? {
        screenType: ErrorScreenType.Error,
        messageHeadline: defaultTitle,
        messageBody: error.message ?? defaultMessage,
        ...buttons,
      }
    : null;

  const onPressHyperLink = (articleId) => {
    try {
      if (articleId) {
        showZendeskArticle(parseInt(articleId as string));
      }
    } catch {
      // TODO JT-109826 log error to analytics
    }
  };

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <ScrollView style={styles.scrollView}>
        <Stack.Screen
          options={{
            headerShown: true,
            headerBackVisible: true,
            title: translatedText('title'),
            header: (props: NativeStackHeaderProps) => (
              <ExpoNavigationBar {...props} />
            ),
          }}
        />
        <ScreenTransitionOverlay
          isLoading={fetching}
          LoadingComponent={<LoadingSpinner />}
          ErrorComponent={
            errorModel && !fetching ? <ErrorScreen {...errorModel} /> : null
          }
        />
        {moveMoneyOptionList?.map((option, index) => (
          <View style={styles.subContainer} key={option?.id}>
            <BodyBaseBoldText numberOfLines={0}>
              {option?.title}
            </BodyBaseBoldText>
            <BodyBaseText numberOfLines={0}>{option?.description}</BodyBaseText>
            <HTMLRenderer
              key={option?.id}
              contentWidth={Dimensions.get('window').width}
              htmlAsString={option?.availabilityHtml}
            />
            {(option?.articleLink?.article as ZendeskArticle).articleId ? (
              <LinkButton
                linkOnPress={() =>
                  onPressHyperLink(
                    (option?.articleLink?.article as ZendeskArticle).articleId,
                  )
                }
                size="base"
                text={option.articleLink.copy}
                linkOnDark={false}
              />
            ) : null}
            {index < moveMoneyOptionList.length - 1 ? (
              <HairLineDivider />
            ) : null}
          </View>
        ))}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  scrollView: {
    paddingHorizontal: theme.spacing.m,
  },
  subContainer: {
    gap: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
});

export default CompareOptionsScreen;
